/** Host component that loads a public/editable form definition before rendering it. */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useUiLocale } from '../../app/ui-locale';
import { getEditableForm, getFilledFormsCount, getSubmittedFormData } from '../../api/forms-api';
import { ErrorMessage, Loading } from '../../components/Loading';
import type { DynamicFormDefinition, FilledFormCapacity, FormValues } from '../../models/form';
import { normalizeFormHeaderBackgroundColor, normalizeFormTitleColor, normalizeTitleAlignment } from '../../utils/form-settings';
import { printDynamicFormsElement } from '../../utils/print-document';
import { FormRenderer } from './FormRenderer';
import { publicCopy } from './public-copy';

interface Props {
  formId: number;
  submittedFormId?: number;
  edit?: boolean;
  moduleId?: number;
}

export function PublicFormApp({ formId, submittedFormId = 0, edit = false, moduleId = 0 }: Props) {
  const { locale, formatNumber, formatDate } = useUiLocale();
  const copy = publicCopy(locale);
  const [form, setForm] = useState<DynamicFormDefinition>();
  const [initialValues, setInitialValues] = useState<FormValues>();
  const [capacity, setCapacity] = useState<FilledFormCapacity>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>();
  const [submitCode, setSubmitCode] = useState<string>();
  const [submittedValues, setSubmittedValues] = useState<FormValues>();
  const printResultRef = useRef<HTMLElement>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(undefined);
    Promise.all([
      getEditableForm(formId),
      submittedFormId ? getSubmittedFormData(submittedFormId) : Promise.resolve(undefined),
      getFilledFormsCount(formId).catch(() => undefined)
    ]).then(([definition, values, availability]) => {
      if (!alive) return;
      setForm(definition);
      setInitialValues(values);
      setCapacity(availability);
    }).catch((loadError) => alive && setError(loadError)).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [formId, submittedFormId]);

  useEffect(() => {
    // InstantSuspension controls only live refresh. Capacity is always enforced by the server.
    if (edit || submitCode != null || capacity?.InstantSuspension !== true) return;
    let alive = true;
    const refresh = () => getFilledFormsCount(formId).then((value) => { if (alive) setCapacity(value); }).catch(() => undefined);
    const timer = window.setInterval(refresh, 5000);
    return () => { alive = false; window.clearInterval(timer); };
  }, [capacity?.InstantSuspension, edit, formId, submitCode]);

  const capacityPercent = useMemo(() => {
    if (!capacity?.IsEnable || capacity.Capacity <= 0) return 0;
    return Math.min(100, Math.round((capacity.Count / capacity.Capacity) * 100));
  }, [capacity]);

  if (loading) return <Loading />;
  if (error) return <ErrorMessage error={error} />;
  if (!form) return <div className="alert alert-warning">{copy.formNotFound}</div>;

  const titleAlignment = normalizeTitleAlignment(form.TitleAlignment);
  const titleStyle: CSSProperties = {
    textAlign: titleAlignment === 'inherit' ? undefined : titleAlignment,
    color: normalizeFormTitleColor(form.TitleColor) || undefined
  };
  const headerStyle: CSSProperties = {
    backgroundColor: normalizeFormHeaderBackgroundColor(form.HeaderBackgroundColor) || undefined
  };

  if (submitCode != null) {
    return <div className="df-submit-result">
      <div className="alert alert-success df-submit-success" role="status">
        <h3>{edit ? copy.changesSavedTitle : copy.formSubmittedTitle}</h3>
        {form.ShowSubmitCode && submitCode ? <p>{copy.trackingCode} <strong className="df-submit-code">{submitCode}</strong></p> : null}
        {form.ShowFinalStage ? <p>{copy.submittedDataAvailable}</p> : <p>{copy.thankYou}</p>}
      </div>
      {form.ShowFinalStage && submittedValues ? <section ref={printResultRef} className="df-submission-success-summary df-print-source" aria-label={copy.submittedData}>
        <div className="df-submission-success-summary__header">
          <div><h3 style={titleStyle}>{form.Title}</h3><p>{copy.finalDataHelp}</p></div>
          <button type="button" className="btn btn-default no-print" onClick={() => printDynamicFormsElement(printResultRef.current, { title: form.Title })}>{copy.printData}</button>
        </div>
        <FormRenderer form={form} initialValues={submittedValues} readOnly showAllReadOnlyTabs omitVerificationFields />
      </section> : null}
    </div>;
  }

  const unavailable = !edit && capacity?.CanSubmit === false;
  return <div className="df-public-form">
    {form.ShowHeader ? <div className="jumbotron df-form-hero" style={headerStyle}>
      <h1 style={titleStyle}>{form.Title}</h1>
      {form.Description ? <p>{form.Description}</p> : null}
      {form.ShowDateRange ? (!form.IsInfinite ? <p className="help-block">{copy.fromDate} <strong>{formatDate(form.StartDate)}</strong> {copy.toDate} <strong>{formatDate(form.EndDate)}</strong></p> : <p className="help-block">{copy.noTimeLimit}</p>) : null}
      {capacity?.IsEnable ? <div className="df-capacity-status" aria-label={`${copy.submittedCount} ${formatNumber(capacity.Count)} ${copy.of} ${formatNumber(capacity.Capacity)}`}>
        <div><span>{copy.submittedCount}</span><strong>{formatNumber(capacity.Count)} {copy.of} {formatNumber(capacity.Capacity)}</strong></div>
        <div className="progress"><div className={`progress-bar ${capacityPercent >= 100 ? 'progress-bar-danger' : 'progress-bar-info'}`} role="progressbar" aria-valuenow={capacityPercent} aria-valuemin={0} aria-valuemax={100} style={{ width: `${capacityPercent}%` }}>{capacityPercent}%</div></div>
      </div> : null}
    </div> : null}

    {unavailable ? <div className="alert alert-warning df-form-unavailable" role="alert"><h3>{copy.unavailableTitle}</h3><p>{capacity?.Message || form.ExpireMessage || form.CapacityMessage || copy.unavailableFallback}</p></div> : <FormRenderer form={form} initialValues={initialValues} submittedFormId={edit ? submittedFormId : 0} moduleId={moduleId} onSubmitted={(code, values) => { setSubmittedValues(values); setSubmitCode(code); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />}
  </div>;
}
